import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material"
import { useState } from "react"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { DocumentAnalyzedObject, loadFunctionalitiesForACF, LoadFunctionalitiesForACFPayload, loadProcessesForACP, LoadProcessesForACPPayload } from "../documentQuestionsApi"

export interface LoadFunctionalitiesForACFDialogProps {
    documentId: AppId
    sectionId: string
    onSuccessfulSave: (analysedObjects: DocumentAnalyzedObject[]) => void
    onCancelClick: () => void,
}

export const LoadFunctionalitiesForACFDialog = ({ documentId, sectionId, onCancelClick, onSuccessfulSave }: LoadFunctionalitiesForACFDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [overwriteExisting, setOverwriteExisting] = useState(true)
    const [onlyFunctionalitiesFromCriticalModules, setOnlyFunctionalitiesFromCriticalModules] = useState(true)

    const handleOkClick = async () => {
        setSaving(true);
        try {
            const params: LoadFunctionalitiesForACFPayload = {
                overwriteExisting,
                onlyFunctionalitiesFromCriticalModules,
            };

            const result = await dispatch(
                loadFunctionalitiesForACF({ documentId, sectionId, params })
            );

            if (loadFunctionalitiesForACF.fulfilled.match(result)) {
                const analysedObjects = result.payload as DocumentAnalyzedObject[];
                dispatch(showSuccess("saved"));
                onSuccessfulSave(analysedObjects);
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }

        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog key="load" open={true} maxWidth="xs" fullWidth keepMounted={false}>
                <DialogTitle
                    sx={{
                        bgcolor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75,
                    }}
                >
                    <Localized id="document-analyzed-objects-load-functionalities">
                        <span>_load</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            alignItems: "center",
                            ".MuiAlert-message": { width: "100%" },
                        }}
                    >
                        <Localized id="document-analyzed-objects-load-functionalities-desc">
                            <span>_load_desc</span>
                        </Localized>
                    </Alert>
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={overwriteExisting}
                                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                                />
                            }
                            label={
                                <Localized id="overwriteExisting">
                                    <span>_Nadpisz istniejące funkcjonalności</span>
                                </Localized>
                            }
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyFunctionalitiesFromCriticalModules}
                                    onChange={(e) => setOnlyFunctionalitiesFromCriticalModules(e.target.checked)}
                                />
                            }
                            label={
                                <Localized id="onlyFunctionalitiesFromCriticalModules">
                                    <span>_Tylko funkcjonalności z krytycznych procesów</span>
                                </Localized>
                            }
                        />
                    </Box>

                    {serverError && (
                        <Alert sx={{ marginTop: 1 }} severity="error">
                            <Localized id={serverError}>
                                <span style={{ whiteSpace: 'pre-line' }}>_{serverError}</span>
                            </Localized>
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                    <SaveButton loading={saving} onClick={handleOkClick} />
                    <CancelButton loading={saving} onClick={onCancelClick} />
                </DialogActions>
            </Dialog>
        </>
    )
}
